import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useIsTruncated } from '@/hooks/useIsTruncated';
import { cn } from '@/lib/utils';

import { ConditionMode, FilterOption } from '../types';

interface CheckboxFilterOptionProps {
  option: FilterOption;
  isChecked: boolean;
  onToggle: (value: string, checked: boolean) => void;
}

/** A single checkbox row. Lives in its own component so each can track label truncation. */
const CheckboxFilterOption = ({ option, isChecked, onToggle }: CheckboxFilterOptionProps) => {
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>('horizontal', [option.label]);
  const Icon = option.icon;

  return (
    <div className="group flex min-h-8 items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-highlight-background">
      <Checkbox
        id={`filter-${option.value}`}
        className={cn(
          'transition-opacity',
          isChecked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
        )}
        checked={isChecked}
        onCheckedChange={(checked) => onToggle(option.value, checked as boolean)}
      />
      <Label
        htmlFor={`filter-${option.value}`}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-2"
      >
        {Icon &&
          (typeof Icon === 'string' ? (
            <span className="text-lg">{Icon}</span>
          ) : (
            <Icon className="h-4 w-4 shrink-0 text-foreground-muted" />
          ))}
        <InfoTooltip content={isTruncated ? option.label : undefined} side="top" size="auto">
          <span ref={ref} className="min-w-0 flex-1 truncate font-medium">
            {option.label}
          </span>
        </InfoTooltip>
      </Label>
    </div>
  );
};

interface CheckboxFilterProps {
  options: FilterOption[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  condition?: boolean;
  conditionMode?: ConditionMode;
  onConditionModeChange?: (mode: ConditionMode) => void;
  onSelectOpenChange?: (isOpen: boolean) => void;
}

export const CheckboxFilter = ({
  options,
  selectedValues = [],
  onSelectionChange,
  condition,
  conditionMode = 'is',
  onConditionModeChange,
  onSelectOpenChange,
}: CheckboxFilterProps) => {
  const { t } = useTranslation();

  const conditionOptions = [
    { label: t('common.is'), value: 'is' },
    { label: t('common.is_not'), value: 'is not' },
  ];
  const handleToggle = (value: string, checked: boolean) => {
    const newValues = checked
      ? [...selectedValues, value]
      : selectedValues.filter((v) => v !== value);
    onSelectionChange(newValues);
  };

  const handleModeChange = (newMode: string) => {
    const typedMode = newMode as ConditionMode;
    if (typedMode !== conditionMode) {
      onConditionModeChange?.(typedMode);
    }
  };

  return (
    <div className="p-2">
      {/* Condition selector */}
      {condition && (
        <div className="mb-3">
          <Select
            value={conditionMode}
            onValueChange={handleModeChange}
            onOpenChange={onSelectOpenChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {conditionOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Options list */}
      {options.map((option) => (
        <CheckboxFilterOption
          key={option.value}
          option={option}
          isChecked={selectedValues.includes(option.value)}
          onToggle={handleToggle}
        />
      ))}
    </div>
  );
};
